import { gql, useQuery } from '@apollo/client'
import { withErrorBoundary } from '@sentry/react'
import moment from 'moment-timezone'
import { useEffect, useState } from 'react'

import ExclamationDot from 'src/assets/exclamation_dot.png'
import ExclamationStick from 'src/assets/exclamation_stick.png'

import SentryFallback from '../SentryFallback'

const GET_FAILED_INVOICE_COUNT = gql`
    query failedInvoiceCount {
        invoices: payments_invoice(
            order_by: { createdAt: desc }
            limit: 1
            where: { comment: { _neq: "UPGRADE" } }
        ) {
            status
            comment
        }
    }
`
const planDetails = {}

const HeaderBanner = () => {
    useQuery(GET_FAILED_INVOICE_COUNT, {
        onCompleted: (data) => {
            const { invoices } = data
            if (invoices && invoices.length > 0) {
                const latestInvoice = invoices[0]
                if (latestInvoice.status === 'FAILED') {
                    setShowAutoPaymentFailedBanner(true)
                }
            }
        },
    })

    const [showAutoPaymentFailedBanner, setShowAutoPaymentFailedBanner] =
        useState(false)
    // show banner for remaning time in days for plan
    const [showTrialPlanEndBanner, setShowTrialPlanEndBanner] = useState(false)

    // get time for the last time user cancelled the banner
    const bannerCancelConfig = JSON.parse(
        localStorage.getItem('bannerCancelConfig')
    )

    // calculate days remaining for the core plan to expire with added grace period of 7 days
    const daysForCorePlanToExpire = moment(planDetails?.core?.validUntil)
        .add(7, 'days')
        .diff(moment(), 'days')

    // calculate days remaining for the trial plan to expire
    const daysForTrialPlanToExpire = moment(planDetails?.core?.validUntil).diff(
        moment(),
        'days'
    )

    useEffect(() => {
        if (
            planDetails?.core?.isTrial &&
            daysForTrialPlanToExpire <= 7 &&
            !bannerCancelConfig
        ) {
            setShowTrialPlanEndBanner(true)
            return
        }

        if (
            planDetails?.core?.isTrial &&
            daysForTrialPlanToExpire <= 3 &&
            bannerCancelConfig.daysRemaining > 3
        ) {
            setShowTrialPlanEndBanner(true)
        }
    }, [bannerCancelConfig, daysForTrialPlanToExpire])

    return (
        <div>
            {showTrialPlanEndBanner && (
                <div className="relative z-10 flex w-full items-center justify-between bg-primary-red p-3 text-13-15 font-bold">
                    <div className="flex items-center gap-6 pl-2">
                        <div>
                            <img
                                src={ExclamationStick}
                                style={{ marginBottom: '2px' }}
                            />
                            <img src={ExclamationDot} />
                        </div>
                        <div>
                            Your subscription will downgrade to Basic with all
                            applicable limitations in {daysForTrialPlanToExpire}{' '}
                            days. Update your payment method now and keep using
                            ORNA to its fullest!
                        </div>
                    </div>
                    <div
                        className="relative -left-0.5 -top-0.5 cursor-pointer"
                        onClick={() => {
                            const cancelObject = {
                                time: moment().unix(),
                                daysRemaining: daysForTrialPlanToExpire,
                            }

                            window.localStorage.setItem(
                                'bannerCancelConfig',
                                JSON.stringify(cancelObject)
                            )
                            setShowTrialPlanEndBanner(false)
                        }}
                    >
                        <svg
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            width="20px"
                            height="20px"
                            viewBox="0 0 20 20"
                            aria-labelledby="closeIconTitle"
                            stroke="#ffffff"
                            strokeWidth="3"
                            strokeLinecap="square"
                            strokeLinejoin="miter"
                            fill="none"
                            color="#ffffff"
                        >
                            {' '}
                            <title id="closeIconTitle">Close</title>{' '}
                            <path d="M6.34314575 6.34314575L17.6568542 17.6568542M6.34314575 17.6568542L17.6568542 6.34314575" />{' '}
                        </svg>
                    </div>
                </div>
            )}
            {showAutoPaymentFailedBanner && (
                <div className="relative z-10 flex w-full items-center justify-between bg-primary-red p-3 text-13-15 font-bold">
                    <div className="flex items-center gap-6 pl-2">
                        <div>
                            <img
                                src={ExclamationStick}
                                style={{ marginBottom: '2px' }}
                            />
                            <img src={ExclamationDot} />
                        </div>
                        <div>
                            Your subscription will downgrade to Basic with all
                            applicable limitations in {daysForCorePlanToExpire}{' '}
                            days. Update your payment method now and keep using
                            ORNA to its fullest!
                        </div>
                    </div>
                    <div
                        className="relative -left-0.5 -top-0.5 cursor-pointer"
                        onClick={() => {
                            setShowAutoPaymentFailedBanner(false)
                        }}
                    >
                        <svg
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            width="20px"
                            height="20px"
                            viewBox="0 0 20 20"
                            aria-labelledby="closeIconTitle"
                            stroke="#ffffff"
                            strokeWidth="3"
                            strokeLinecap="square"
                            strokeLinejoin="miter"
                            fill="none"
                            color="#ffffff"
                        >
                            {' '}
                            <title id="closeIconTitle">Close</title>{' '}
                            <path d="M6.34314575 6.34314575L17.6568542 17.6568542M6.34314575 17.6568542L17.6568542 6.34314575" />{' '}
                        </svg>
                    </div>
                </div>
            )}
        </div>
    )
}

export default withErrorBoundary(HeaderBanner, {
    fallback: <SentryFallback />,
})
